import { useTranslation } from "react-i18next";
import { Switch } from "@tellescope/ui";
import { Select, Label, Input } from "@tellescope/design-system";
import type { ContentListBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "@tellescope/visibility-schema";

type ContentListBlockEditorProps = BlockTypeEditorProps<ContentListBlock>;

export function ContentListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: ContentListBlockEditorProps) {
  const { t } = useTranslation();

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Title (optional)")}</Label>
        <Input
          value={block.props.title || ""}
          onChange={(e) => updateProps({ title: e.target.value || undefined })}
          placeholder={t("Your Content")}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 10)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="20">20</Select.Item>
            <Select.Item value="50">50</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No content available")}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("Header Photo Style")}</Label>
        <Select
          value={block.props.headerPhotoStyle ?? "banner"}
          onValueChange={(value) => updateProps({ headerPhotoStyle: value as "banner" | "icon" })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="banner">{t("Banner (full width above content)")}</Select.Item>
            <Select.Item value="icon">{t("Icon (replaces type icon)")}</Select.Item>
          </Select.Content>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Label>{t("Show Search Bar")}</Label>
        <Switch
          checked={block.props.showSearch ?? false}
          onCheckedChange={(checked) => updateProps({ showSearch: checked })}
        />
      </div>

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which content items are displayed based on conditions. Use 'Content' source for content properties."
        )}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
